import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import MDTypography from "../../../../components/MDTypography";
import { sheetsSettingsApi } from "../../../../services/api/plenty/sheets_settings";

const PennerRennerSheetSettingsSubTab = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Separate settings for Amazon, Plenty and CSV sheets
  const [amazonSheetSettings, setAmazonSheetSettings] = useState({
    spreadsheetId: "",
    version: "v4",
  });
  const [plentySheetSettings, setPlentySheetSettings] = useState({
    spreadsheetId: "",
    version: "v4",
  });

  const [csvSheetSettings, setCsvSheetSettings] = useState({
    spreadsheetId: "",
    sheetId: "",
    version: "v4",
  });

  // URL states for all sheets
  const [amazonSheetsUrl, setAmazonSheetsUrl] = useState("");
  const [plentySheetsUrl, setPlentySheetsUrl] = useState("");
  const [csvSheetsUrl, setCsvSheetsUrl] = useState("");
  const [amazonSheetsUrlError, setAmazonSheetsUrlError] = useState("");
  const [plentySheetsUrlError, setPlentySheetsUrlError] = useState("");
  const [csvSheetsUrlError, setCsvSheetsUrlError] = useState("");

  // Plenty column settings
  const [plentyColumnSettings, setPlentyColumnSettings] = useState({
    dateColumn: "",
    skuColumn: "",
    stockColumn: "",
    soldArticlesColumn: "",
    ratioColumn: "",
    grossRevenueColumn: "",
    variantIdColumn: "",
    shoppingPriceColumn: "",
    shoppingLocationColumn: "",
    shoppingDateColumn: "",
  });

  // Amazon column settings
  const [amazonColumnSettings, setAmazonColumnSettings] = useState({
    dateColumn: "",
    skuColumn: "",
    stockColumn: "",
    ratioColumn: "",
    asinColumn: "",
    sentToFbaColumn: "",
    recommendedReorderColumn: "",
    shoppingPriceColumn: "",
    shoppingLocationColumn: "",
    shoppingDateColumn: "",
  });

  useEffect(() => {
    // Load initial data with error handling
    const initializeData = async () => {
      try {
        // Load all data in parallel but handle each error separately
        const loadOperations = [
          loadSheetsSettings().catch((error) => {
            console.error("Failed to load sheets settings on init:", error);
            return null; // Continue with other operations
          }),
        ];

        await Promise.allSettled(loadOperations);
      } catch (error) {
        console.error("Unexpected error during initialization:", error);
        setError(getErrorMessage(error));
      }
    };

    initializeData();
  }, []);

  // Helper functions for dual sheet management
  const handleAmazonSheetSettingsChange = (field, value) => {
    setAmazonSheetSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePlentySheetSettingsChange = (field, value) => {
    setPlentySheetSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCsvSheetSettingsChange = (field, value) => {
    setCsvSheetSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAmazonSheetsUrlChange = (url) => {
    setAmazonSheetsUrl(url);
    if (url.trim()) {
      try {
        const result = extractIdsFromSheetsUrl(url);

        if (result.success) {
          setAmazonSheetSettings((prev) => ({
            ...prev,
            spreadsheetId: result.spreadsheetId,
          }));
        } else {
          setAmazonSheetsUrlError(
            "Could not extract Spreadsheet ID and Sheet ID from URL",
          );
        }
      } catch (error) {
        setAmazonSheetsUrlError("Invalid Google Sheets URL format");
      }
    } else {
      setAmazonSheetsUrlError("");
    }
  };

  const handlePlentySheetsUrlChange = (url) => {
    setPlentySheetsUrl(url);
    if (url.trim()) {
      try {
        const result = extractIdsFromSheetsUrl(url);

        if (result.success) {
          setPlentySheetSettings((prev) => ({
            ...prev,
            spreadsheetId: result.spreadsheetId,
          }));
        } else {
          setPlentySheetsUrlError(
            "Could not extract Spreadsheet ID and Sheet ID from URL",
          );
        }
      } catch (error) {
        setPlentySheetsUrlError("Invalid Google Sheets URL format");
      }
    } else {
      setPlentySheetsUrlError("");
    }
  };

  const handlecsvSheetsUrlChange = (url) => {
    setCsvSheetsUrl(url);
    if (url.trim()) {
      try {
        const result = extractIdsFromSheetsUrl(url);

        if (result.success) {
          setCsvSheetSettings((prev) => ({
            ...prev,
            spreadsheetId: result.spreadsheetId,
            sheetId: result.sheetId,
          }));
        } else {
          setCsvSheetsUrlError(
            "Could not extract Spreadsheet ID and Sheet ID from URL",
          );
        }
      } catch (error) {
        setCsvSheetsUrlError("Invalid Google Sheets URL format");
      }
    } else {
      setCsvSheetsUrlError("");
    }
  };

  //Sheets
  const loadSheetsSettings = async () => {
    try {
      console.log("Loading sheets settings for Amazon, Plenty, and CSV...");

      // Load Amazon settings
      const amazonResponse = await sheetsSettingsApi.getSettings("amazon");
      const amazonSettings = amazonResponse.data;

      // Load Plenty settings
      const plentyResponse = await sheetsSettingsApi.getSettings("plenty");
      const plentySettings = plentyResponse.data;

      // Load CSV settings
      const csvResponse = await sheetsSettingsApi.getSettings("csv");
      const csvSettings = csvResponse.data;

      // Update Amazon sheet settings (no sheetId - managed automatically)
      setAmazonSheetSettings({
        spreadsheetId: amazonSettings.spreadsheet_id || "",
        version: amazonSettings.version || "v4",
      });

      // Update Plenty sheet settings (no sheetId - managed automatically)
      setPlentySheetSettings({
        spreadsheetId: plentySettings.spreadsheet_id || "",
        version: plentySettings.version || "v4",
      });

      // Update CSV sheet settings
      setCsvSheetSettings({
        spreadsheetId: csvSettings.spreadsheet_id || "",
        sheetId: csvSettings.sheet_id || "",
        version: csvSettings.version || "v4",
      });

      // Update Plenty column settings
      setPlentyColumnSettings({
        dateColumn: plentySettings.date_column || "",
        skuColumn: plentySettings.asin_sku_column || "",
        stockColumn: plentySettings.stock_column || "",
        soldArticlesColumn: plentySettings.sold_articles_column || "",
        ratioColumn: plentySettings.ratio_column || "",
        grossRevenueColumn: plentySettings.gross_revenue_column || "",
        variantIdColumn: plentySettings.variant_id_column || "",
        shoppingPriceColumn: plentySettings.shopping_price_column || "",
        shoppingLocationColumn: plentySettings.shopping_location_column || "",
        shoppingDateColumn: plentySettings.shopping_date_column || "",
      });

      // Update Amazon column settings
      setAmazonColumnSettings({
        dateColumn: amazonSettings.date_column || "",
        skuColumn: amazonSettings.asin_sku_column || "",
        stockColumn: amazonSettings.stock_column || "",
        ratioColumn: amazonSettings.ratio_column || "",
        asinColumn: amazonSettings.asin_column || "",
        sentToFbaColumn: amazonSettings.sent_to_fba_column || "",
        recommendedReorderColumn:
          amazonSettings.recommended_reorder_column || "",
        shoppingPriceColumn: amazonSettings.shopping_price_column || "",
        shoppingLocationColumn: amazonSettings.shopping_location_column || "",
        shoppingDateColumn: amazonSettings.shopping_date_column || "",
      });
    } catch (error) {
      console.error("Failed to load sheets settings:", error);
      setError(getErrorMessage(error));
    }
  };

  const handleAmazonColumnSettingsChange = (field, value) => {
    setAmazonColumnSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePlentyColumnSettingsChange = (field, value) => {
    setPlentyColumnSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Google Sheets URL parsing functions
  const extractIdsFromSheetsUrl = (url) => {
    try {
      // Pattern: https://docs.google.com/spreadsheets/d/SPREADSHEET_ID/edit?gid=SHEET_ID#gid=SHEET_ID
      const spreadsheetIdMatch = url.match(
        /\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/,
      );
      const sheetIdMatch = url.match(/[?#]gid=([0-9]+)/);

      if (spreadsheetIdMatch && sheetIdMatch) {
        return {
          spreadsheetId: spreadsheetIdMatch[1],
          sheetId: sheetIdMatch[1],
          success: true,
          error: null,
        };
      } else if (spreadsheetIdMatch && !sheetIdMatch) {
        return {
          spreadsheetId: spreadsheetIdMatch[1],
          sheetId: "0", // Default to first sheet
          success: true,
          error: null,
        };
      } else {
        return {
          spreadsheetId: "",
          sheetId: "",
          success: false,
          error: "Could not extract IDs from URL. Please check the format.",
        };
      }
    } catch (error) {
      return {
        spreadsheetId: "",
        sheetId: "",
        success: false,
        error: "Invalid URL format.",
      };
    }
  };

  const validateColumnInput = (value) => {
    // Validate column letter format (A-Z, case insensitive)
    const columnRegex = /^[A-Za-z]+$/;
    return columnRegex.test(value.trim()) || value.trim() === "";
  };

  const saveAmazonSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const amazonSettingsData = {
        spreadsheet_id: amazonSheetSettings.spreadsheetId || null,
        version: amazonSheetSettings.version || "v4",
        date_column: amazonColumnSettings.dateColumn || null,
        asin_sku_column: amazonColumnSettings.skuColumn || null,
        stock_column: amazonColumnSettings.stockColumn || null,
        ratio_column: amazonColumnSettings.ratioColumn || null,
        asin_column: amazonColumnSettings.asinColumn || null,
        sent_to_fba_column: amazonColumnSettings.sentToFbaColumn || null,
        recommended_reorder_column:
          amazonColumnSettings.recommendedReorderColumn || null,
        shopping_price_column: amazonColumnSettings.shoppingPriceColumn || null,
        shopping_location_column:
          amazonColumnSettings.shoppingLocationColumn || null,
        shopping_date_column: amazonColumnSettings.shoppingDateColumn || null,
      };

      await sheetsSettingsApi.updateSettings(amazonSettingsData, "amazon");

      console.log("Amazon settings saved successfully");
      setSuccess("Amazon settings saved successfully!");
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error("Failed to save Amazon settings:", error);
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const savePlentySettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const plentySettingsData = {
        spreadsheet_id: plentySheetSettings.spreadsheetId || null,
        version: plentySheetSettings.version || "v4",
        date_column: plentyColumnSettings.dateColumn || null,
        asin_sku_column: plentyColumnSettings.skuColumn || null,
        stock_column: plentyColumnSettings.stockColumn || null,
        sold_articles_column: plentyColumnSettings.soldArticlesColumn || null,
        ratio_column: plentyColumnSettings.ratioColumn || null,
        gross_revenue_column: plentyColumnSettings.grossRevenueColumn || null,
        variant_id_column: plentyColumnSettings.variantIdColumn || null,
        shopping_price_column: plentyColumnSettings.shoppingPriceColumn || null,
        shopping_location_column:
          plentyColumnSettings.shoppingLocationColumn || null,
        shopping_date_column: plentyColumnSettings.shoppingDateColumn || null,
      };

      await sheetsSettingsApi.updateSettings(plentySettingsData, "plenty");

      console.log("Plenty settings saved successfully");
      setSuccess("Plenty settings saved successfully!");
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error("Failed to save Plenty settings:", error);
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const saveCsvSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const csvSettingsData = {
        spreadsheet_id: csvSheetSettings.spreadsheetId || null,
        sheet_id: csvSheetSettings.sheetId || null,
        version: csvSheetSettings.version || "v4",
      };

      await sheetsSettingsApi.updateSettings(csvSettingsData, "csv");

      console.log("CSV settings saved successfully");
      setSuccess("CSV settings saved successfully!");
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error("Failed to save CSV settings:", error);
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const clearAmazonSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log("Resetting Amazon settings...");
      await sheetsSettingsApi.resetSettings("amazon");

      setAmazonSheetSettings({
        spreadsheetId: "",
        version: "v4",
      });
      setAmazonSheetsUrl("");
      setAmazonSheetsUrlError("");
      setAmazonColumnSettings({
        dateColumn: "",
        skuColumn: "",
        stockColumn: "",
        ratioColumn: "",
        asinColumn: "",
        sentToFbaColumn: "",
        recommendedReorderColumn: "",
        shoppingPriceColumn: "",
        shoppingLocationColumn: "",
        shoppingDateColumn: "",
      });

      setSuccess("Amazon settings cleared!");
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error("Failed to reset Amazon settings:", error);
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const clearPlentySettings = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log("Resetting Plenty settings...");
      await sheetsSettingsApi.resetSettings("plenty");

      setPlentySheetSettings({
        spreadsheetId: "",
        version: "v4",
      });
      setPlentySheetsUrl("");
      setPlentySheetsUrlError("");
      setPlentyColumnSettings({
        dateColumn: "",
        skuColumn: "",
        stockColumn: "",
        soldArticlesColumn: "",
        ratioColumn: "",
        grossRevenueColumn: "",
        variantIdColumn: "",
        shoppingPriceColumn: "",
        shoppingLocationColumn: "",
        shoppingDateColumn: "",
      });

      setSuccess("Plenty settings cleared!");
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error("Failed to reset Plenty settings:", error);
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const clearCsvSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log("Resetting CSV settings...");
      await sheetsSettingsApi.resetSettings("csv");

      setCsvSheetSettings({
        spreadsheetId: "",
        sheetId: "",
        version: "v4",
      });
      setCsvSheetsUrl("");
      setCsvSheetsUrlError("");

      setSuccess("CSV settings cleared!");
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error("Failed to reset CSV settings:", error);
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const getErrorMessage = (error) => {
    if (error.response) {
      return (
        error.response.data?.detail ||
        error.response.data?.message ||
        "Server error occurred"
      );
    }
    return error.message || "An unexpected error occurred";
  };

  return (
    <Card>
      <CardContent>
        <MDTypography variant="h5" fontWeight="medium" color="dark" mb={3}>
          Google Sheets Settings
        </MDTypography>

        {/* Explanation Card - Always Full Width at Top */}
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            How Sheet Management Works
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>Amazon & Plenty Sheets:</strong> The system automatically
            creates and manages multiple sheets within your specified
            spreadsheet for categorizing articles:
          </Typography>
          <Typography variant="body2" component="div" sx={{ ml: 2 }}>
            • <strong>Penner</strong> - High-performing articles (high ratio)
            <br />• <strong>Renner</strong> - Low-performing articles (low
            ratio)
            <br />• <strong>Neutral</strong> - Articles between thresholds
            <br />• <strong>OOS</strong> - Out of stock articles
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            You only need to provide the Spreadsheet ID - the system will create
            and manage the individual sheets automatically.
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            <strong>CSV Sheet:</strong> Requires both a Spreadsheet ID and a
            specific Sheet ID for CSV upload operations.
          </Typography>
        </Alert>

        <Grid container spacing={3}>
          {/* Amazon Sheet Configuration - Upper Left */}
          <Grid item xs={12} md={6}>
            <Card sx={{ backgroundColor: "#FF9900", color: "white", mb: 2 }}>
              <CardContent>
                <MDTypography
                  variant="h6"
                  fontWeight="medium"
                  mb={2}
                  sx={{ color: "white" }}
                >
                  Amazon Google Sheets Configuration
                </MDTypography>

                {/* URL Input Section */}
                <Paper
                  sx={{
                    p: 2,
                    mb: 2,
                    backgroundColor: "rgba(255,255,255,0.1)",
                  }}
                >
                  <MDTypography
                    variant="body1"
                    fontWeight="medium"
                    mb={1}
                    sx={{ color: "white" }}
                  >
                    Option 1: Paste Google Sheets URL
                  </MDTypography>

                  <TextField
                    fullWidth
                    label="Amazon Google Sheets URL"
                    placeholder="https://docs.google.com/spreadsheets/d/..."
                    value={amazonSheetsUrl}
                    onChange={(e) =>
                      handleAmazonSheetsUrlChange(e.target.value)
                    }
                    error={!!amazonSheetsUrlError}
                    helperText={
                      amazonSheetsUrlError || "URL will be automatically parsed"
                    }
                    sx={{
                      mb: 1,
                      "& .MuiInputBase-input": { color: "white" },
                      "& .MuiInputLabel-root": {
                        color: "rgba(255,255,255,0.7)",
                      },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "rgba(255,255,255,0.3)",
                        },
                        "&:hover fieldset": {
                          borderColor: "rgba(255,255,255,0.5)",
                        },
                      },
                    }}
                  />
                </Paper>

                {/* Manual Input Section */}
                <Paper
                  sx={{
                    p: 2,
                    mb: 2,
                    backgroundColor: "rgba(255,255,255,0.1)",
                  }}
                >
                  <MDTypography
                    variant="body1"
                    fontWeight="medium"
                    mb={1}
                    sx={{ color: "white" }}
                  >
                    Option 2: Manual Input
                  </MDTypography>

                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Amazon Spreadsheet ID"
                        placeholder="1TSodLjztKpEgCWMZdfaktzJSdVXKUUgzHMIcPfsBLQM"
                        value={amazonSheetSettings.spreadsheetId}
                        onChange={(e) =>
                          handleAmazonSheetSettingsChange(
                            "spreadsheetId",
                            e.target.value,
                          )
                        }
                        size="small"
                        sx={{
                          mb: 1,
                          "& .MuiInputBase-input": { color: "white" },
                          "& .MuiInputLabel-root": {
                            color: "rgba(255,255,255,0.7)",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "rgba(255,255,255,0.3)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(255,255,255,0.5)",
                            },
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth size="small">
                        <InputLabel sx={{ color: "rgba(255,255,255,0.7)" }}>
                          API Version
                        </InputLabel>
                        <Select
                          value={amazonSheetSettings.version}
                          label="API Version"
                          onChange={(e) =>
                            handleAmazonSheetSettingsChange(
                              "version",
                              e.target.value,
                            )
                          }
                          sx={{
                            color: "white",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(255,255,255,0.3)",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(255,255,255,0.5)",
                            },
                            "& .MuiSvgIcon-root": { color: "white" },
                          }}
                        >
                          <MenuItem value="v4">v4</MenuItem>
                          <MenuItem value="v3">v3</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Current Settings Display */}
                <Box
                  sx={{
                    pt: 1,
                    borderTop: "1px solid rgba(255,255,255,0.2)",
                  }}
                >
                  <MDTypography
                    variant="body2"
                    fontWeight="medium"
                    mb={1}
                    sx={{ color: "white" }}
                  >
                    Current Settings:
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    sx={{
                      color: "rgba(255,255,255,0.8)",
                      fontSize: "0.75rem",
                    }}
                  >
                    {amazonSheetSettings.spreadsheetId ? (
                      <a
                        href={`https://docs.google.com/spreadsheets/d/${amazonSheetSettings.spreadsheetId}/edit`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "inherit",
                          textDecoration: "underline",
                        }}
                      >
                        {`https://docs.google.com/spreadsheets/d/${amazonSheetSettings.spreadsheetId}/edit`}
                      </a>
                    ) : (
                      "Not set"
                    )}{" "}
                    {" | "}
                    Version: {amazonSheetSettings.version}
                  </MDTypography>
                </Box>

                {/* Amazon Column Settings */}
                <Box
                  sx={{
                    pt: 2,
                    mt: 2,
                    borderTop: "2px solid rgba(255,255,255,0.3)",
                  }}
                >
                  <MDTypography
                    variant="h6"
                    fontWeight="medium"
                    mb={2}
                    sx={{ color: "white" }}
                  >
                    Amazon Column Settings
                  </MDTypography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Date Column"
                        placeholder="A"
                        value={amazonColumnSettings.dateColumn}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          if (validateColumnInput(value)) {
                            handleAmazonColumnSettingsChange(
                              "dateColumn",
                              value,
                            );
                          }
                        }}
                        helperText="e.g., A"
                        size="small"
                        inputProps={{
                          style: {
                            textTransform: "uppercase",
                            color: "white",
                          },
                        }}
                        sx={{
                          "& .MuiInputLabel-root": {
                            color: "rgba(255,255,255,0.7)",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "rgba(255,255,255,0.3)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(255,255,255,0.5)",
                            },
                          },
                          "& .MuiFormHelperText-root": {
                            color: "rgba(255,255,255,0.6)",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="SKU Column"
                        placeholder="B"
                        value={amazonColumnSettings.skuColumn}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          if (validateColumnInput(value)) {
                            handleAmazonColumnSettingsChange(
                              "skuColumn",
                              value,
                            );
                          }
                        }}
                        helperText="e.g., B"
                        size="small"
                        inputProps={{
                          style: {
                            textTransform: "uppercase",
                            color: "white",
                          },
                        }}
                        sx={{
                          "& .MuiInputLabel-root": {
                            color: "rgba(255,255,255,0.7)",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "rgba(255,255,255,0.3)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(255,255,255,0.5)",
                            },
                          },
                          "& .MuiFormHelperText-root": {
                            color: "rgba(255,255,255,0.6)",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="ASIN Column"
                        placeholder="D"
                        value={amazonColumnSettings.asinColumn}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          if (validateColumnInput(value)) {
                            handleAmazonColumnSettingsChange(
                              "asinColumn",
                              value,
                            );
                          }
                        }}
                        helperText="e.g., D"
                        size="small"
                        inputProps={{
                          style: {
                            textTransform: "uppercase",
                            color: "white",
                          },
                        }}
                        sx={{
                          "& .MuiInputLabel-root": {
                            color: "rgba(255,255,255,0.7)",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "rgba(255,255,255,0.3)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(255,255,255,0.5)",
                            },
                          },
                          "& .MuiFormHelperText-root": {
                            color: "rgba(255,255,255,0.6)",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Stock Column"
                        placeholder="C"
                        value={amazonColumnSettings.stockColumn}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          if (validateColumnInput(value)) {
                            handleAmazonColumnSettingsChange(
                              "stockColumn",
                              value,
                            );
                          }
                        }}
                        helperText="e.g., C"
                        size="small"
                        inputProps={{
                          style: {
                            textTransform: "uppercase",
                            color: "white",
                          },
                        }}
                        sx={{
                          "& .MuiInputLabel-root": {
                            color: "rgba(255,255,255,0.7)",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "rgba(255,255,255,0.3)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(255,255,255,0.5)",
                            },
                          },
                          "& .MuiFormHelperText-root": {
                            color: "rgba(255,255,255,0.6)",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Ratio Column"
                        placeholder="E"
                        value={amazonColumnSettings.ratioColumn}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          if (validateColumnInput(value)) {
                            handleAmazonColumnSettingsChange(
                              "ratioColumn",
                              value,
                            );
                          }
                        }}
                        helperText="e.g., E"
                        size="small"
                        inputProps={{
                          style: {
                            textTransform: "uppercase",
                            color: "white",
                          },
                        }}
                        sx={{
                          "& .MuiInputLabel-root": {
                            color: "rgba(255,255,255,0.7)",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "rgba(255,255,255,0.3)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(255,255,255,0.5)",
                            },
                          },
                          "& .MuiFormHelperText-root": {
                            color: "rgba(255,255,255,0.6)",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Sent to FBA Column"
                        placeholder="F"
                        value={amazonColumnSettings.sentToFbaColumn}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          if (validateColumnInput(value)) {
                            handleAmazonColumnSettingsChange(
                              "sentToFbaColumn",
                              value,
                            );
                          }
                        }}
                        helperText="e.g., F"
                        size="small"
                        inputProps={{
                          style: {
                            textTransform: "uppercase",
                            color: "white",
                          },
                        }}
                        sx={{
                          "& .MuiInputLabel-root": {
                            color: "rgba(255,255,255,0.7)",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "rgba(255,255,255,0.3)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(255,255,255,0.5)",
                            },
                          },
                          "& .MuiFormHelperText-root": {
                            color: "rgba(255,255,255,0.6)",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Recommended Reorder Column"
                        placeholder="G"
                        value={amazonColumnSettings.recommendedReorderColumn}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          if (validateColumnInput(value)) {
                            handleAmazonColumnSettingsChange(
                              "recommendedReorderColumn",
                              value,
                            );
                          }
                        }}
                        helperText="e.g., G"
                        size="small"
                        inputProps={{
                          style: {
                            textTransform: "uppercase",
                            color: "white",
                          },
                        }}
                        sx={{
                          "& .MuiInputLabel-root": {
                            color: "rgba(255,255,255,0.7)",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "rgba(255,255,255,0.3)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(255,255,255,0.5)",
                            },
                          },
                          "& .MuiFormHelperText-root": {
                            color: "rgba(255,255,255,0.6)",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Shopping Price Column"
                        placeholder="H"
                        value={amazonColumnSettings.shoppingPriceColumn}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          if (validateColumnInput(value)) {
                            handleAmazonColumnSettingsChange(
                              "shoppingPriceColumn",
                              value,
                            );
                          }
                        }}
                        helperText="e.g., H"
                        size="small"
                        inputProps={{
                          style: {
                            textTransform: "uppercase",
                            color: "white",
                          },
                        }}
                        sx={{
                          "& .MuiInputLabel-root": {
                            color: "rgba(255,255,255,0.7)",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "rgba(255,255,255,0.3)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(255,255,255,0.5)",
                            },
                          },
                          "& .MuiFormHelperText-root": {
                            color: "rgba(255,255,255,0.6)",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Shopping Location Column"
                        placeholder="I"
                        value={amazonColumnSettings.shoppingLocationColumn}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          if (validateColumnInput(value)) {
                            handleAmazonColumnSettingsChange(
                              "shoppingLocationColumn",
                              value,
                            );
                          }
                        }}
                        helperText="e.g., I"
                        size="small"
                        inputProps={{
                          style: {
                            textTransform: "uppercase",
                            color: "white",
                          },
                        }}
                        sx={{
                          "& .MuiInputLabel-root": {
                            color: "rgba(255,255,255,0.7)",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "rgba(255,255,255,0.3)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(255,255,255,0.5)",
                            },
                          },
                          "& .MuiFormHelperText-root": {
                            color: "rgba(255,255,255,0.6)",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Shopping Date Column"
                        placeholder="J"
                        value={amazonColumnSettings.shoppingDateColumn}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          if (validateColumnInput(value)) {
                            handleAmazonColumnSettingsChange(
                              "shoppingDateColumn",
                              value,
                            );
                          }
                        }}
                        helperText="e.g., J"
                        size="small"
                        inputProps={{
                          style: {
                            textTransform: "uppercase",
                            color: "white",
                          },
                        }}
                        sx={{
                          "& .MuiInputLabel-root": {
                            color: "rgba(255,255,255,0.7)",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "rgba(255,255,255,0.3)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(255,255,255,0.5)",
                            },
                          },
                          "& .MuiFormHelperText-root": {
                            color: "rgba(255,255,255,0.6)",
                          },
                        }}
                      />
                    </Grid>
                  </Grid>

                  {/* Amazon Action Buttons */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      justifyContent: "flex-end",
                      mt: 3,
                    }}
                  >
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: "white",
                        color: "#FF9900",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.9)",
                        },
                      }}
                      onClick={saveAmazonSettings}
                      disabled={loading || !amazonSheetSettings.spreadsheetId}
                      startIcon={<DescriptionIcon />}
                    >
                      {loading ? "Saving..." : "Save Amazon Settings"}
                    </Button>

                    <Button
                      variant="outlined"
                      sx={{
                        color: "white",
                        borderColor: "rgba(255,255,255,0.5)",
                        "&:hover": {
                          borderColor: "white",
                          backgroundColor: "rgba(255,255,255,0.1)",
                        },
                      }}
                      onClick={clearAmazonSettings}
                      disabled={loading}
                      startIcon={<DeleteIcon />}
                    >
                      Clear
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Plenty Sheet Configuration - Upper Right */}
          <Grid item xs={12} md={6}>
            <Card sx={{ backgroundColor: "#00A3E0", color: "white", mb: 2 }}>
              <CardContent>
                <MDTypography
                  variant="h6"
                  fontWeight="medium"
                  mb={2}
                  sx={{ color: "white" }}
                >
                  Plenty Google Sheets Configuration
                </MDTypography>

                {/* URL Input Section */}
                <Paper
                  sx={{
                    p: 2,
                    mb: 2,
                    backgroundColor: "rgba(255,255,255,0.1)",
                  }}
                >
                  <MDTypography
                    variant="body1"
                    fontWeight="medium"
                    mb={1}
                    sx={{ color: "white" }}
                  >
                    Option 1: Paste Google Sheets URL
                  </MDTypography>

                  <TextField
                    fullWidth
                    label="Plenty Google Sheets URL"
                    placeholder="https://docs.google.com/spreadsheets/d/..."
                    value={plentySheetsUrl}
                    onChange={(e) =>
                      handlePlentySheetsUrlChange(e.target.value)
                    }
                    error={!!plentySheetsUrlError}
                    helperText={
                      plentySheetsUrlError || "URL will be automatically parsed"
                    }
                    sx={{
                      mb: 1,
                      "& .MuiInputBase-input": { color: "white" },
                      "& .MuiInputLabel-root": {
                        color: "rgba(255,255,255,0.7)",
                      },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "rgba(255,255,255,0.3)",
                        },
                        "&:hover fieldset": {
                          borderColor: "rgba(255,255,255,0.5)",
                        },
                      },
                    }}
                  />
                </Paper>

                {/* Manual Input Section */}
                <Paper
                  sx={{
                    p: 2,
                    mb: 2,
                    backgroundColor: "rgba(255,255,255,0.1)",
                  }}
                >
                  <MDTypography
                    variant="body1"
                    fontWeight="medium"
                    mb={1}
                    sx={{ color: "white" }}
                  >
                    Option 2: Manual Input
                  </MDTypography>

                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Plenty Spreadsheet ID"
                        placeholder="1TSodLjztKpEgCWMZdfaktzJSdVXKUUgzHMIcPfsBLQM"
                        value={plentySheetSettings.spreadsheetId}
                        onChange={(e) =>
                          handlePlentySheetSettingsChange(
                            "spreadsheetId",
                            e.target.value,
                          )
                        }
                        size="small"
                        sx={{
                          mb: 1,
                          "& .MuiInputBase-input": { color: "white" },
                          "& .MuiInputLabel-root": {
                            color: "rgba(255,255,255,0.7)",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "rgba(255,255,255,0.3)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(255,255,255,0.5)",
                            },
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth size="small">
                        <InputLabel sx={{ color: "rgba(255,255,255,0.7)" }}>
                          API Version
                        </InputLabel>
                        <Select
                          value={plentySheetSettings.version}
                          label="API Version"
                          onChange={(e) =>
                            handlePlentySheetSettingsChange(
                              "version",
                              e.target.value,
                            )
                          }
                          sx={{
                            color: "white",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(255,255,255,0.3)",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(255,255,255,0.5)",
                            },
                            "& .MuiSvgIcon-root": { color: "white" },
                          }}
                        >
                          <MenuItem value="v4">v4</MenuItem>
                          <MenuItem value="v3">v3</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Current Settings Display */}
                <Box
                  sx={{
                    pt: 1,
                    borderTop: "1px solid rgba(255,255,255,0.2)",
                  }}
                >
                  <MDTypography
                    variant="body2"
                    fontWeight="medium"
                    mb={1}
                    sx={{ color: "white" }}
                  >
                    Current Settings:
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    sx={{
                      color: "rgba(255,255,255,0.8)",
                      fontSize: "0.75rem",
                    }}
                  >
                    {plentySheetSettings.spreadsheetId ? (
                      <a
                        href={`https://docs.google.com/spreadsheets/d/${plentySheetSettings.spreadsheetId}/edit`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "inherit",
                          textDecoration: "underline",
                        }}
                      >
                        {`https://docs.google.com/spreadsheets/d/${plentySheetSettings.spreadsheetId}/edit`}
                      </a>
                    ) : (
                      "Not set"
                    )}{" "}
                    {" | "}
                    Version: {plentySheetSettings.version}
                  </MDTypography>
                </Box>

                {/* Plenty Column Settings */}
                <Box
                  sx={{
                    pt: 2,
                    mt: 2,
                    borderTop: "2px solid rgba(255,255,255,0.3)",
                  }}
                >
                  <MDTypography
                    variant="h6"
                    fontWeight="medium"
                    mb={2}
                    sx={{ color: "white" }}
                  >
                    Plenty Column Settings
                  </MDTypography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Date Column"
                        placeholder="A"
                        value={plentyColumnSettings.dateColumn}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          if (validateColumnInput(value)) {
                            handlePlentyColumnSettingsChange(
                              "dateColumn",
                              value,
                            );
                          }
                        }}
                        helperText="e.g., A"
                        size="small"
                        inputProps={{
                          style: {
                            textTransform: "uppercase",
                            color: "white",
                          },
                        }}
                        sx={{
                          "& .MuiInputLabel-root": {
                            color: "rgba(255,255,255,0.7)",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "rgba(255,255,255,0.3)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(255,255,255,0.5)",
                            },
                          },
                          "& .MuiFormHelperText-root": {
                            color: "rgba(255,255,255,0.6)",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="SKU Column"
                        placeholder="B"
                        value={plentyColumnSettings.skuColumn}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          if (validateColumnInput(value)) {
                            handlePlentyColumnSettingsChange(
                              "skuColumn",
                              value,
                            );
                          }
                        }}
                        helperText="e.g., B"
                        size="small"
                        inputProps={{
                          style: {
                            textTransform: "uppercase",
                            color: "white",
                          },
                        }}
                        sx={{
                          "& .MuiInputLabel-root": {
                            color: "rgba(255,255,255,0.7)",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "rgba(255,255,255,0.3)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(255,255,255,0.5)",
                            },
                          },
                          "& .MuiFormHelperText-root": {
                            color: "rgba(255,255,255,0.6)",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label=" Variation ID Column"
                        placeholder="C"
                        value={plentyColumnSettings.variantIdColumn}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          if (validateColumnInput(value)) {
                            handlePlentyColumnSettingsChange(
                              "variantIdColumn",
                              value,
                            );
                          }
                        }}
                        helperText="e.g., C"
                        size="small"
                        inputProps={{
                          style: {
                            textTransform: "uppercase",
                            color: "white",
                          },
                        }}
                        sx={{
                          "& .MuiInputLabel-root": {
                            color: "rgba(255,255,255,0.7)",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "rgba(255,255,255,0.3)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(255,255,255,0.5)",
                            },
                          },
                          "& .MuiFormHelperText-root": {
                            color: "rgba(255,255,255,0.6)",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Stock Column"
                        placeholder="D"
                        value={plentyColumnSettings.stockColumn}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          if (validateColumnInput(value)) {
                            handlePlentyColumnSettingsChange(
                              "stockColumn",
                              value,
                            );
                          }
                        }}
                        helperText="e.g., D"
                        size="small"
                        inputProps={{
                          style: {
                            textTransform: "uppercase",
                            color: "white",
                          },
                        }}
                        sx={{
                          "& .MuiInputLabel-root": {
                            color: "rgba(255,255,255,0.7)",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "rgba(255,255,255,0.3)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(255,255,255,0.5)",
                            },
                          },
                          "& .MuiFormHelperText-root": {
                            color: "rgba(255,255,255,0.6)",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Sold Articles Column"
                        placeholder="E"
                        value={plentyColumnSettings.soldArticlesColumn}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          if (validateColumnInput(value)) {
                            handlePlentyColumnSettingsChange(
                              "soldArticlesColumn",
                              value,
                            );
                          }
                        }}
                        helperText="e.g., E"
                        size="small"
                        inputProps={{
                          style: {
                            textTransform: "uppercase",
                            color: "white",
                          },
                        }}
                        sx={{
                          "& .MuiInputLabel-root": {
                            color: "rgba(255,255,255,0.7)",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "rgba(255,255,255,0.3)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(255,255,255,0.5)",
                            },
                          },
                          "& .MuiFormHelperText-root": {
                            color: "rgba(255,255,255,0.6)",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Ratio Column"
                        placeholder="F"
                        value={plentyColumnSettings.ratioColumn}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          if (validateColumnInput(value)) {
                            handlePlentyColumnSettingsChange(
                              "ratioColumn",
                              value,
                            );
                          }
                        }}
                        helperText="e.g., F"
                        size="small"
                        inputProps={{
                          style: {
                            textTransform: "uppercase",
                            color: "white",
                          },
                        }}
                        sx={{
                          "& .MuiInputLabel-root": {
                            color: "rgba(255,255,255,0.7)",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "rgba(255,255,255,0.3)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(255,255,255,0.5)",
                            },
                          },
                          "& .MuiFormHelperText-root": {
                            color: "rgba(255,255,255,0.6)",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Gross Revenue Column"
                        placeholder="G"
                        value={plentyColumnSettings.grossRevenueColumn}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          if (validateColumnInput(value)) {
                            handlePlentyColumnSettingsChange(
                              "grossRevenueColumn",
                              value,
                            );
                          }
                        }}
                        helperText="e.g., G"
                        size="small"
                        inputProps={{
                          style: {
                            textTransform: "uppercase",
                            color: "white",
                          },
                        }}
                        sx={{
                          "& .MuiInputLabel-root": {
                            color: "rgba(255,255,255,0.7)",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "rgba(255,255,255,0.3)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(255,255,255,0.5)",
                            },
                          },
                          "& .MuiFormHelperText-root": {
                            color: "rgba(255,255,255,0.6)",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Shopping Price Column"
                        placeholder="H"
                        value={plentyColumnSettings.shoppingPriceColumn}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          if (validateColumnInput(value)) {
                            handlePlentyColumnSettingsChange(
                              "shoppingPriceColumn",
                              value,
                            );
                          }
                        }}
                        helperText="e.g., H"
                        size="small"
                        inputProps={{
                          style: {
                            textTransform: "uppercase",
                            color: "white",
                          },
                        }}
                        sx={{
                          "& .MuiInputLabel-root": {
                            color: "rgba(255,255,255,0.7)",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "rgba(255,255,255,0.3)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(255,255,255,0.5)",
                            },
                          },
                          "& .MuiFormHelperText-root": {
                            color: "rgba(255,255,255,0.6)",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Shopping Location Column"
                        placeholder="I"
                        value={plentyColumnSettings.shoppingLocationColumn}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          if (validateColumnInput(value)) {
                            handlePlentyColumnSettingsChange(
                              "shoppingLocationColumn",
                              value,
                            );
                          }
                        }}
                        helperText="e.g., I"
                        size="small"
                        inputProps={{
                          style: {
                            textTransform: "uppercase",
                            color: "white",
                          },
                        }}
                        sx={{
                          "& .MuiInputLabel-root": {
                            color: "rgba(255,255,255,0.7)",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "rgba(255,255,255,0.3)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(255,255,255,0.5)",
                            },
                          },
                          "& .MuiFormHelperText-root": {
                            color: "rgba(255,255,255,0.6)",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Shopping Date Column"
                        placeholder="J"
                        value={plentyColumnSettings.shoppingDateColumn}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          if (validateColumnInput(value)) {
                            handlePlentyColumnSettingsChange(
                              "shoppingDateColumn",
                              value,
                            );
                          }
                        }}
                        helperText="e.g., J"
                        size="small"
                        inputProps={{
                          style: {
                            textTransform: "uppercase",
                            color: "white",
                          },
                        }}
                        sx={{
                          "& .MuiInputLabel-root": {
                            color: "rgba(255,255,255,0.7)",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "rgba(255,255,255,0.3)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(255,255,255,0.5)",
                            },
                          },
                          "& .MuiFormHelperText-root": {
                            color: "rgba(255,255,255,0.6)",
                          },
                        }}
                      />
                    </Grid>
                  </Grid>

                  {/* Plenty Action Buttons */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      justifyContent: "flex-end",
                      mt: 3,
                    }}
                  >
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: "white",
                        color: "#00A3E0",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.9)",
                        },
                      }}
                      onClick={savePlentySettings}
                      disabled={loading || !plentySheetSettings.spreadsheetId}
                      startIcon={<DescriptionIcon />}
                    >
                      {loading ? "Saving..." : "Save Plenty Settings"}
                    </Button>

                    <Button
                      variant="outlined"
                      sx={{
                        color: "white",
                        borderColor: "rgba(255,255,255,0.5)",
                        "&:hover": {
                          borderColor: "white",
                          backgroundColor: "rgba(255,255,255,0.1)",
                        },
                      }}
                      onClick={clearPlentySettings}
                      disabled={loading}
                      startIcon={<DeleteIcon />}
                    >
                      Clear
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* CSV Sheet Configuration - Lower Full Width */}
          <Grid item xs={12} md={6}>
            <Card sx={{ backgroundColor: "#75716dff", color: "white", mb: 2 }}>
              <CardContent>
                <MDTypography
                  variant="h6"
                  fontWeight="medium"
                  mb={2}
                  sx={{ color: "white" }}
                >
                  CSV Google Sheets Configuration
                </MDTypography>

                {/* URL Input Section */}
                <Paper
                  sx={{
                    p: 2,
                    mb: 2,
                    backgroundColor: "rgba(255,255,255,0.1)",
                  }}
                >
                  <MDTypography
                    variant="body1"
                    fontWeight="medium"
                    mb={1}
                    sx={{ color: "white" }}
                  >
                    Option 1: Paste Google Sheets URL
                  </MDTypography>

                  <TextField
                    fullWidth
                    label="CSV Google Sheets URL"
                    placeholder="https://docs.google.com/spreadsheets/d/..."
                    value={csvSheetsUrl}
                    onChange={(e) => handlecsvSheetsUrlChange(e.target.value)}
                    error={!!csvSheetsUrlError}
                    helperText={
                      csvSheetsUrlError || "URL will be automatically parsed"
                    }
                    sx={{
                      mb: 1,
                      "& .MuiInputBase-input": { color: "white" },
                      "& .MuiInputLabel-root": {
                        color: "rgba(255,255,255,0.7)",
                      },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "rgba(255,255,255,0.3)",
                        },
                        "&:hover fieldset": {
                          borderColor: "rgba(255,255,255,0.5)",
                        },
                      },
                    }}
                  />
                </Paper>

                {/* Manual Input Section */}
                <Paper
                  sx={{
                    p: 2,
                    mb: 2,
                    backgroundColor: "rgba(255,255,255,0.1)",
                  }}
                >
                  <MDTypography
                    variant="body1"
                    fontWeight="medium"
                    mb={1}
                    sx={{ color: "white" }}
                  >
                    Option 2: Manual Input
                  </MDTypography>

                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="CSV Spreadsheet ID"
                        placeholder="1TSodLjztKpEgCWMZdfaktzJSdVXKUUgzHMIcPfsBLQM"
                        value={csvSheetSettings.spreadsheetId}
                        onChange={(e) =>
                          handleCsvSheetSettingsChange(
                            "spreadsheetId",
                            e.target.value,
                          )
                        }
                        size="small"
                        sx={{
                          mb: 1,
                          "& .MuiInputBase-input": { color: "white" },
                          "& .MuiInputLabel-root": {
                            color: "rgba(255,255,255,0.7)",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "rgba(255,255,255,0.3)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(255,255,255,0.5)",
                            },
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Sheet ID"
                        placeholder="304143252"
                        value={csvSheetSettings.sheetId}
                        onChange={(e) =>
                          handleCsvSheetSettingsChange(
                            "sheetId",
                            e.target.value,
                          )
                        }
                        size="small"
                        sx={{
                          "& .MuiInputBase-input": { color: "white" },
                          "& .MuiInputLabel-root": {
                            color: "rgba(255,255,255,0.7)",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "rgba(255,255,255,0.3)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(255,255,255,0.5)",
                            },
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel sx={{ color: "rgba(255,255,255,0.7)" }}>
                          API Version
                        </InputLabel>
                        <Select
                          value={csvSheetSettings.version}
                          label="API Version"
                          onChange={(e) =>
                            handleCsvSheetSettingsChange(
                              "version",
                              e.target.value,
                            )
                          }
                          sx={{
                            color: "white",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(255,255,255,0.3)",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(255,255,255,0.5)",
                            },
                            "& .MuiSvgIcon-root": { color: "white" },
                          }}
                        >
                          <MenuItem value="v4">v4</MenuItem>
                          <MenuItem value="v3">v3</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Current Settings Display */}
                <Box
                  sx={{
                    pt: 1,
                    borderTop: "1px solid rgba(255,255,255,0.2)",
                  }}
                >
                  <MDTypography
                    variant="body2"
                    fontWeight="medium"
                    mb={1}
                    sx={{ color: "white" }}
                  >
                    Current Settings:
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    sx={{
                      color: "rgba(255,255,255,0.8)",
                      fontSize: "0.75rem",
                    }}
                  >
                    {csvSheetSettings.spreadsheetId ? (
                      <a
                        href={`https://docs.google.com/spreadsheets/d/${
                          csvSheetSettings.spreadsheetId
                        }/edit#gid=${csvSheetSettings.sheetId || "0"}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "inherit",
                          textDecoration: "underline",
                        }}
                      >
                        {`https://docs.google.com/spreadsheets/d/${
                          csvSheetSettings.spreadsheetId
                        }/edit#gid=${csvSheetSettings.sheetId || "0"}`}
                      </a>
                    ) : (
                      "Not set"
                    )}{" "}
                    {" | "}
                    Version: {csvSheetSettings.version}
                  </MDTypography>
                </Box>

                {/* CSV Action Buttons */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-end",
                    mt: 3,
                    pt: 2,
                    borderTop: "2px solid rgba(255,255,255,0.3)",
                  }}
                >
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: "white",
                      color: "#75716d",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.9)",
                      },
                    }}
                    onClick={saveCsvSettings}
                    disabled={loading || !csvSheetSettings.spreadsheetId}
                    startIcon={<DescriptionIcon />}
                  >
                    {loading ? "Saving..." : "Save CSV Settings"}
                  </Button>

                  <Button
                    variant="outlined"
                    sx={{
                      color: "white",
                      borderColor: "rgba(255,255,255,0.5)",
                      "&:hover": {
                        borderColor: "white",
                        backgroundColor: "rgba(255,255,255,0.1)",
                      },
                    }}
                    onClick={clearCsvSettings}
                    disabled={loading}
                    startIcon={<DeleteIcon />}
                  >
                    Clear
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Success/Error Messages */}
          {(success || error) && (
            <Grid item xs={12}>
              {success && (
                <Alert severity="success" onClose={() => setSuccess(null)}>
                  {success}
                </Alert>
              )}
              {error && (
                <Alert severity="error" onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default PennerRennerSheetSettingsSubTab;
